import React, { useEffect, useState } from "react";
import { FiUnlock, FiX } from "react-icons/fi";
import { ClipLoader } from "react-spinners";
import {
  UNLOCK_REASON_MAX_LENGTH,
  getBangkokDateKey,
  validateUnlockForm,
} from "../../../utils/timesheetApprovalAccess";

const UnlockSubmissionModal = ({
  submission,
  employeeName,
  periodLabel,
  submitting = false,
  serverErrors,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState("");
  const [reopenUntil, setReopenUntil] = useState("");
  const [errors, setErrors] = useState({});
  const today = getBangkokDateKey();

  useEffect(() => {
    setReason("");
    setReopenUntil("");
    setErrors({});
  }, [submission]);

  useEffect(() => {
    setErrors(serverErrors || {});
  }, [serverErrors]);

  useEffect(() => {
    if (!submission) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, submission, submitting]);

  if (!submission) return null;

  const handleConfirm = () => {
    if (submitting) return;
    const nextErrors = validateUnlockForm({ reason, reopenUntil }, today);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    onConfirm({ reason: reason.trim(), reopenUntil });
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true" aria-labelledby="unlock-submission-title">
      <div className="w-full max-w-lg rounded-3xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"><FiUnlock /></div>
            <div>
              <h2 id="unlock-submission-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">ยืนยันการปลดล็อก Timesheet</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{employeeName}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">ช่วงสัปดาห์ {periodLabel}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={submitting} aria-label="ปิด" className="rounded-lg p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 dark:hover:text-gray-200"><FiX size={20} /></button>
        </div>

        <label className="mt-5 block text-sm font-medium text-gray-700 dark:text-gray-300">
          เหตุผลการปลดล็อก <span className="text-red-500">*</span>
          <textarea
            value={reason}
            maxLength={UNLOCK_REASON_MAX_LENGTH}
            onChange={(event) => {
              setReason(event.target.value);
              if (errors.reason) setErrors((prev) => ({ ...prev, reason: "" }));
            }}
            disabled={submitting}
            aria-invalid={Boolean(errors.reason)}
            className={`mt-2 min-h-28 w-full rounded-xl border bg-white p-3 text-sm dark:bg-gray-900 ${errors.reason ? "border-red-500 ring-2 ring-red-200 dark:border-red-400 dark:ring-red-900/50" : "border-gray-200 dark:border-gray-700"}`}
          />
        </label>
        <div className="mt-1 flex justify-between gap-3 text-xs">
          <span className="text-red-600 dark:text-red-300">{errors.reason}</span>
          <span className="shrink-0 text-gray-400">{reason.length}/{UNLOCK_REASON_MAX_LENGTH}</span>
        </div>

        <label className="mt-4 block text-sm font-medium text-gray-700 dark:text-gray-300">
          เปิดให้แก้ถึงวันที่ (ไม่บังคับ)
          <input
            type="date"
            value={reopenUntil}
            min={today}
            onChange={(event) => {
              setReopenUntil(event.target.value);
              if (errors.reopenUntil) setErrors((prev) => ({ ...prev, reopenUntil: "" }));
            }}
            disabled={submitting}
            aria-invalid={Boolean(errors.reopenUntil)}
            className={`mt-2 w-full rounded-xl border bg-white px-3 py-2 text-sm dark:bg-gray-900 ${errors.reopenUntil ? "border-red-500 ring-2 ring-red-200 dark:border-red-400 dark:ring-red-900/50" : "border-gray-200 dark:border-gray-700"}`}
          />
        </label>
        {errors.reopenUntil && <p className="mt-1 text-xs text-red-600 dark:text-red-300">{errors.reopenUntil}</p>}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">ใช้เฉพาะสัปดาห์ที่เลย deadline แล้ว ถ้าไม่ระบุจะเปิดให้ 7 วัน</p>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={submitting} className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700">ยกเลิก</button>
          <button type="button" onClick={handleConfirm} disabled={submitting} className="inline-flex min-w-28 items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60">
            {submitting ? <ClipLoader size={16} color="#ffffff" /> : <FiUnlock />}
            {submitting ? "กำลังปลดล็อก..." : "ยืนยันปลดล็อก"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnlockSubmissionModal;
